import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

export interface ExhibitionFavorites {
  countries: string[]; // country codes
  booths: string[]; // booth IDs
}

const STORAGE_KEY = "exhibition_favorites";

export const useExhibitionFavorites = () => {
  const { user } = useAuthContext();
  const [favorites, setFavorites] = useState<ExhibitionFavorites>({
    countries: [],
    booths: [],
  });
  const [loading, setLoading] = useState(false);

  // Load favorites from database when logged in, or localStorage when not
  useEffect(() => {
    if (user) {
      loadFromDatabase();
    } else {
      loadFromLocalStorage();
    }
  }, [user]);

  const loadFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setFavorites({
          countries: parsed.countries || [],
          booths: parsed.booths || [],
        });
      }
    } catch (error) {
      console.error("Error loading favorites from localStorage:", error);
    }
  };

  const loadFromDatabase = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_favorites")
        .select("favorite_type, favorite_id")
        .eq("user_id", user.id);

      if (error) throw error;

      const countries: string[] = [];
      const booths: string[] = [];

      data?.forEach((fav) => {
        if (fav.favorite_type === "country") {
          countries.push(fav.favorite_id);
        } else if (fav.favorite_type === "booth") {
          booths.push(fav.favorite_id);
        }
      });

      setFavorites({ countries, booths });

      // Also sync to localStorage for offline access
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ countries, booths }));
    } catch (error) {
      console.error("Error loading favorites from database:", error);
      // Fall back to localStorage
      loadFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  // Migrate localStorage favorites to database when user logs in
  const migrateLocalToDatabase = useCallback(async () => {
    if (!user) return;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const parsed = JSON.parse(stored);
      const localCountries = parsed.countries || [];
      const localBooths = parsed.booths || [];

      // Get existing database favorites
      const { data: existing } = await supabase
        .from("user_favorites")
        .select("favorite_type, favorite_id")
        .eq("user_id", user.id);

      const existingSet = new Set(
        existing?.map((f) => `${f.favorite_type}:${f.favorite_id}`) || []
      );

      // Insert new favorites that don't exist in database
      const toInsert: { user_id: string; favorite_type: string; favorite_id: string }[] = [];

      localCountries.forEach((code: string) => {
        if (!existingSet.has(`country:${code}`)) {
          toInsert.push({ user_id: user.id, favorite_type: "country", favorite_id: code });
        }
      });

      localBooths.forEach((id: string) => {
        if (!existingSet.has(`booth:${id}`)) {
          toInsert.push({ user_id: user.id, favorite_type: "booth", favorite_id: id });
        }
      });

      if (toInsert.length > 0) {
        await supabase.from("user_favorites").insert(toInsert);
        // Reload from database
        await loadFromDatabase();
      }
    } catch (error) {
      console.error("Error migrating favorites:", error);
    }
  }, [user]);

  // Run migration when user logs in
  useEffect(() => {
    if (user) {
      migrateLocalToDatabase();
    }
  }, [user, migrateLocalToDatabase]);

  const toggleCountryFavorite = useCallback(async (countryCode: string) => {
    const isFavorite = favorites.countries.includes(countryCode);
    
    // Optimistic update
    setFavorites((prev) => {
      const newCountries = isFavorite
        ? prev.countries.filter((c) => c !== countryCode)
        : [...prev.countries, countryCode];
      const newFavorites = { ...prev, countries: newCountries };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newFavorites));
      return newFavorites;
    });

    // Sync to database if logged in
    if (user) {
      try {
        if (isFavorite) {
          await supabase
            .from("user_favorites")
            .delete()
            .eq("user_id", user.id)
            .eq("favorite_type", "country")
            .eq("favorite_id", countryCode);
        } else {
          await supabase.from("user_favorites").insert({
            user_id: user.id,
            favorite_type: "country",
            favorite_id: countryCode,
          });
        }
      } catch (error) {
        console.error("Error syncing country favorite:", error);
        // Revert on error
        loadFromDatabase();
      }
    }
  }, [favorites.countries, user]);

  const toggleBoothFavorite = useCallback(async (boothId: string) => {
    const isFavorite = favorites.booths.includes(boothId);
    
    // Optimistic update
    setFavorites((prev) => {
      const newBooths = isFavorite
        ? prev.booths.filter((b) => b !== boothId)
        : [...prev.booths, boothId];
      const newFavorites = { ...prev, booths: newBooths };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newFavorites));
      return newFavorites;
    });

    // Sync to database if logged in
    if (user) {
      try {
        if (isFavorite) {
          await supabase
            .from("user_favorites")
            .delete()
            .eq("user_id", user.id)
            .eq("favorite_type", "booth")
            .eq("favorite_id", boothId);
        } else {
          await supabase.from("user_favorites").insert({
            user_id: user.id,
            favorite_type: "booth",
            favorite_id: boothId,
          });
        }
      } catch (error) {
        console.error("Error syncing booth favorite:", error);
        // Revert on error
        loadFromDatabase();
      }
    }
  }, [favorites.booths, user]);

  const isCountryFavorite = useCallback(
    (countryCode: string) => favorites.countries.includes(countryCode),
    [favorites.countries]
  );

  const isBoothFavorite = useCallback(
    (boothId: string) => favorites.booths.includes(boothId),
    [favorites.booths]
  );

  return {
    favorites,
    loading,
    toggleCountryFavorite,
    toggleBoothFavorite,
    isCountryFavorite,
    isBoothFavorite,
    favoriteCountryCodes: favorites.countries,
    favoriteBoothIds: favorites.booths,
    refreshFavorites: loadFromDatabase,
  };
};
